import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { StatusPill } from "@/components/StatusPill";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, GripVertical, Calendar as CalendarIcon, MoveRight, CalendarRange, Pencil, Trash2, MoreHorizontal, Trash } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCenter,
} from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";

export default function Planning() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [pods, setPods] = useState([]);
  const [plannings, setPlannings] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [filterCampaign, setFilterCampaign] = useState("all");
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const refresh = async () => {
    const [c, p, pd, l] = await Promise.all([
      api.get("/campaigns"),
      api.get("/plannings"),
      api.get("/masters/pods"),
      api.get("/leaves"),
    ]);
    setCampaigns(c.data);
    setPlannings(p.data);
    setPods(pd.data);
    setLeaves(l.data);
  };

  useEffect(() => { refresh(); }, []);

  const removePlanning = async (planningId, label) => {
    if (!confirm(`Delete planning slot for ${label || "this entry"}?`)) return;
    try {
      await api.delete(`/plannings/${planningId}`);
      toast.success("Planning slot deleted");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} selected planning slot${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const { data } = await api.post("/plannings/bulk-delete", { planning_ids: ids });
      toast.success(`Deleted ${data.deleted} slot${data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  const bulkDeletePod = async (podName) => {
    if (!confirm(`Delete ALL planning slots assigned to "${podName}"${filterCampaign !== "all" ? " (for the selected campaign)" : ""}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const payload = { assigned_team: podName === "Unassigned" ? "" : podName };
      if (filterCampaign !== "all") payload.campaign_id = filterCampaign;
      const { data } = await api.post("/plannings/bulk-delete", payload);
      toast.success(`Deleted ${data.deleted} slot${data.deleted === 1 ? "" : "s"} from ${podName}`);
      clearSelection();
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkDeleting(false); }
  };

  const filteredPlannings = filterCampaign === "all" ? plannings : plannings.filter((p) => p.campaign_id === filterCampaign);
  const canEdit = user && (user.role === "admin" || user.role === "command_center");

  // Group plannings by pod (assigned_team)
  const swimlanes = useMemo(() => {
    const lanes = {};
    pods.forEach((p) => { lanes[p.name] = []; });
    lanes["Unassigned"] = [];
    filteredPlannings.forEach((pl) => {
      const lane = pl.assigned_team || "Unassigned";
      if (!lanes[lane]) lanes[lane] = [];
      lanes[lane].push(pl);
    });
    return lanes;
  }, [pods, filteredPlannings]);

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || !canEdit) return;
    const planningId = active.id;
    const targetLane = over.id;
    const planning = plannings.find((p) => p.id === planningId);
    if (!planning || planning.assigned_team === targetLane) return;
    // Optimistic update
    setPlannings((prev) => prev.map((p) => p.id === planningId ? { ...p, assigned_team: targetLane === "Unassigned" ? "" : targetLane } : p));
    try {
      await api.patch(`/plannings/${planningId}`, { assigned_team: targetLane === "Unassigned" ? "" : targetLane });
      toast.success(`Moved to ${targetLane}`);
    } catch (e) {
      toast.error(formatApiError(e));
      refresh();
    }
  };

  return (
    <div className="space-y-5" data-testid="planning-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Workload Planning</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">Planning Board</h2>
          <p className="text-sm text-zinc-500 mt-1">Drag slots between pod lanes to reassign work.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            {selectedIds.size > 0 && (
              <Button
                onClick={bulkDeleteSelected}
                disabled={bulkDeleting}
                variant="destructive"
                data-testid="bulk-delete-plannings-button"
              >
                <Trash className="h-4 w-4 mr-1.5" />
                {bulkDeleting ? "Deleting…" : `Delete ${selectedIds.size}`}
              </Button>
            )}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
              <DialogTrigger asChild>
                <Button data-testid="bulk-planning-button" variant="outline">
                  <CalendarRange className="h-4 w-4 mr-1.5" /> Bulk Schedule
                </Button>
              </DialogTrigger>
              <BulkPlanningDialog campaigns={campaigns} pods={pods} onClose={() => setBulkOpen(false)} onCreated={refresh} />
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-planning-button" className="bg-zinc-950 hover:bg-zinc-800 text-white">
                  <Plus className="h-4 w-4 mr-1.5" /> Schedule Slot
                </Button>
              </DialogTrigger>
              <NewPlanningDialog campaigns={campaigns} pods={pods} onClose={() => setOpen(false)} onCreated={refresh} />
            </Dialog>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="flex items-center gap-3">
          <Label className="text-xs">Filter by campaign:</Label>
          <Select value={filterCampaign} onValueChange={setFilterCampaign}>
            <SelectTrigger className="w-72" data-testid="filter-planning-campaign"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All campaigns</SelectItem>
              {campaigns.map((c) => <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_id} — {c.campaign_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {filterCampaign !== "all" && (() => {
        const camp = campaigns.find((c) => c.campaign_id === filterCampaign);
        if (!camp) return null;
        const scheduled = filteredPlannings.reduce((sum, p) => sum + (p.daily_target || 0), 0);
        const target = camp.target_installations || 0;
        const remaining = Math.max(target - scheduled, 0);
        const overflow = Math.max(scheduled - target, 0);
        const pct = target ? Math.min(Math.round((scheduled / target) * 100), 100) : 0;
        return (
          <Card className="p-4" data-testid="planning-target-summary">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{camp.media_type === "Cab" ? "Cabs" : camp.media_type === "Bus" ? "Buses" : camp.media_type === "Truck" ? "Trucks" : "Autos"} target coverage</p>
                <p className="font-display text-2xl font-bold tracking-tight mt-0.5">
                  <span data-testid="summary-scheduled">{scheduled}</span>
                  <span className="text-zinc-400 font-normal"> / {target}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {remaining > 0 && <span data-testid="summary-remaining">{remaining} left to schedule</span>}
                  {overflow > 0 && <span className="text-amber-700" data-testid="summary-overflow">+{overflow} above target</span>}
                  {remaining === 0 && overflow === 0 && target > 0 && <span className="text-emerald-700">Fully scheduled</span>}
                </p>
              </div>
              <div className="flex-1 max-w-md">
                <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
                  <div className={`h-full ${overflow > 0 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-zinc-500 mt-1 text-right font-mono">{pct}%</p>
              </div>
            </div>
          </Card>
        );
      })()}

      {filterCampaign === "all" && filteredPlannings.length > 0 && (() => {
        const totalScheduled = filteredPlannings.reduce((sum, p) => sum + (p.daily_target || 0), 0);
        const cidsInPlanning = new Set(filteredPlannings.map((p) => p.campaign_id));
        const totalTarget = campaigns
          .filter((c) => cidsInPlanning.has(c.campaign_id))
          .reduce((sum, c) => sum + (c.target_installations || 0), 0);
        const remaining = Math.max(totalTarget - totalScheduled, 0);
        const overflow = Math.max(totalScheduled - totalTarget, 0);
        const pct = totalTarget ? Math.min(Math.round((totalScheduled / totalTarget) * 100), 100) : 0;
        return (
          <Card className="p-4" data-testid="planning-overall-summary">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Pods active</p>
                <p className="font-display text-2xl font-bold mt-0.5" data-testid="overall-pod-count">
                  {Object.values(swimlanes).filter((l) => l.length > 0).length}
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Slots</p>
                <p className="font-display text-2xl font-bold mt-0.5" data-testid="overall-slot-count">{filteredPlannings.length}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Scheduled units</p>
                <p className="font-display text-2xl font-bold mt-0.5" data-testid="overall-scheduled">{totalScheduled}</p>
                <p className="text-[10px] text-zinc-500 font-mono">of {totalTarget} target</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Coverage</p>
                <p className="font-display text-2xl font-bold mt-0.5">
                  <span className={pct >= 100 ? "text-emerald-700" : ""}>{pct}%</span>
                </p>
                <p className="text-[10px] text-zinc-500">
                  {remaining > 0 && <>{remaining} left</>}
                  {overflow > 0 && <span className="text-amber-700">+{overflow} above</span>}
                </p>
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Swimlanes */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="planning-swimlanes">
          {Object.keys(swimlanes).map((laneName) => (
            <Lane
              key={laneName}
              name={laneName}
              items={swimlanes[laneName]}
              campaigns={campaigns}
              draggable={canEdit}
              onEdit={(p) => setEditing(p)}
              onDelete={(p) => removePlanning(p.id, p.campaign_id)}
              canEdit={canEdit}
              selectedIds={selectedIds}
              onToggleSelect={toggleSelect}
              onDeletePod={bulkDeletePod}
            />
          ))}
        </div>
      </DndContext>

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <EditPlanningDialog
            planning={editing}
            campaigns={campaigns}
            pods={pods}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); refresh(); }}
          />
        </Dialog>
      )}

      {/* Leave / availability */}
      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-display text-sm font-semibold">Team Leave & Availability</h3>
          <CalendarIcon className="h-4 w-4 text-zinc-400" />
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b">
            <tr>
              <th className="text-left py-2.5 px-4 font-semibold">Person</th>
              <th className="text-left py-2.5 px-4 font-semibold">Type</th>
              <th className="text-left py-2.5 px-4 font-semibold">From</th>
              <th className="text-left py-2.5 px-4 font-semibold">To</th>
            </tr>
          </thead>
          <tbody>
            {leaves.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-zinc-500 text-xs">No leave records.</td></tr>}
            {leaves.map((l) => (
              <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`leave-row-${l.id}`}>
                <td className="py-2.5 px-4 font-medium">{l.person}</td>
                <td className="py-2.5 px-4 capitalize">{l.leave_type}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{l.from_date}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{l.to_date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Lane({ name, items, campaigns, draggable, onEdit, onDelete, canEdit, selectedIds, onToggleSelect, onDeletePod }) {
  const { isOver, setNodeRef } = useDroppable({ id: name });
  const totalUnits = items.reduce((sum, p) => sum + (p.daily_target || 0), 0);
  return (
    <div
      ref={setNodeRef}
      data-testid={`lane-${name}`}
      className={`bg-white border rounded-md flex flex-col min-h-[280px] transition-colors ${isOver ? "border-[#0055FF] bg-[#0055FF]/5" : "border-zinc-200"}`}
    >
      <div className="px-3 py-2 border-b border-zinc-200 sticky top-0 bg-white/95 backdrop-blur z-10">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-display font-semibold truncate">{name}</h4>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 font-mono" title="Slots">
              {items.length} slot{items.length === 1 ? "" : "s"}
            </span>
            {totalUnits > 0 && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-white font-mono"
                title="Sum of daily targets in this pod"
                data-testid={`lane-total-${name}`}
              >
                {totalUnits} units
              </span>
            )}
            {canEdit && items.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-1 hover:bg-zinc-100 rounded" data-testid={`lane-actions-${name}`}>
                    <MoreHorizontal className="h-3.5 w-3.5 text-zinc-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => onDeletePod(name)} className="text-red-600" data-testid={`lane-delete-all-${name}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete all in this pod
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
      <div className="p-2 space-y-2 flex-1">
        {items.length === 0 && <div className="text-center text-xs text-zinc-400 py-8">Drop slots here</div>}
        {items.map((p) => {
          const camp = campaigns.find((c) => c.campaign_id === p.campaign_id);
          return (
            <SlotCard
              key={p.id}
              planning={p}
              campaign={camp}
              draggable={draggable}
              canEdit={canEdit}
              onEdit={() => onEdit(p)}
              onDelete={() => onDelete(p)}
              selected={selectedIds?.has(p.id)}
              onToggleSelect={() => onToggleSelect && onToggleSelect(p.id)}
            />
          );
        })}
      </div>
    </div>
  );
}

function SlotCard({ planning, campaign, draggable, canEdit, onEdit, onDelete, selected, onToggleSelect }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: planning.id, disabled: !draggable });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 } : undefined;
  const health = campaign?.health || "scheduled";
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`slot-card-${planning.id}`}
      className={`group bg-white border-l-4 ${health === "red" ? "border-l-red-500" : health === "amber" ? "border-l-amber-500" : "border-l-emerald-500"} border ${selected ? "border-[#0055FF] ring-1 ring-[#0055FF]" : "border-zinc-200"} rounded-md p-2.5 shadow-sm ${isDragging ? "opacity-60 shadow-lg" : ""}`}
    >
      <div className="flex items-start gap-2">
        {canEdit && (
          <div onPointerDown={(e) => e.stopPropagation()} className="pt-0.5">
            <Checkbox
              checked={!!selected}
              onCheckedChange={onToggleSelect}
              data-testid={`slot-select-${planning.id}`}
            />
          </div>
        )}
        {draggable && (
          <button {...listeners} {...attributes} className="text-zinc-300 hover:text-zinc-500 cursor-grab active:cursor-grabbing pt-0.5" data-testid={`drag-handle-${planning.id}`}>
            <GripVertical className="h-4 w-4" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[10px] text-zinc-500">{planning.campaign_id}</div>
              <div className="text-sm font-medium truncate">{campaign?.campaign_name || "Unknown campaign"}</div>
            </div>
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 hover:bg-zinc-100 rounded opacity-60 group-hover:opacity-100 transition-opacity"
                    data-testid={`slot-actions-${planning.id}`}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3.5 w-3.5 text-zinc-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={onEdit} data-testid={`slot-edit-${planning.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={onDelete} className="text-red-600" data-testid={`slot-delete-${planning.id}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-zinc-600">
            <span className="font-mono">{planning.planned_date}</span>
            <span className="capitalize">·  {planning.capacity_slot?.replace("_", " ")}</span>
            {planning.daily_target ? (
              <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-900 text-white text-[10px] font-mono" data-testid={`slot-target-${planning.id}`}>
                {planning.daily_target}
              </span>
            ) : null}
          </div>
          {/* Execution rollup — shows actual completed vs daily target */}
          {(() => {
            const target = Number(planning.daily_target) || 0;
            const ac = Number(planning.actual_completed) || 0;
            const t = Number(planning.actual_completed_t) || 0;
            const nt = Number(planning.actual_completed_nt) || 0;
            const status = planning.execution_status || "not_started";
            if (target === 0 && ac === 0) return null;
            const pill = {
              done:        { label: "✓ Done",    bg: "bg-emerald-100 text-emerald-800 border-emerald-300" },
              over:        { label: "✓ Over-delivered", bg: "bg-emerald-100 text-emerald-800 border-emerald-300" },
              in_progress: { label: "In progress", bg: "bg-blue-100 text-blue-800 border-blue-300" },
              not_started: { label: "Not started", bg: "bg-zinc-100 text-zinc-600 border-zinc-300" },
              untargeted:  { label: "Logged",     bg: "bg-emerald-100 text-emerald-800 border-emerald-300" },
            }[status] || { label: status, bg: "bg-zinc-100 text-zinc-600 border-zinc-300" };
            const pct = target > 0 ? Math.min(Math.round((ac / target) * 100), 999) : 100;
            const barPct = target > 0 ? Math.min((ac / target) * 100, 100) : 100;
            const overflow = target > 0 && ac > target;
            return (
              <div className="mt-1.5 space-y-1" data-testid={`slot-execution-${planning.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium border ${pill.bg}`}>
                    {pill.label}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-600">
                    <span className={status === "not_started" ? "text-zinc-400" : "text-zinc-900 font-semibold"}>{ac}</span>
                    {target > 0 && <span className="text-zinc-400">/{target}</span>}
                    {target > 0 && <span className={`ml-1 ${overflow ? "text-amber-700" : "text-zinc-500"}`}>· {pct}%</span>}
                  </span>
                </div>
                {(t > 0 || nt > 0) && (
                  <div className="text-[10px] text-zinc-500 font-mono">T:{t} · N.T:{nt}</div>
                )}
                {target > 0 && (
                  <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                    <div className={`h-full ${overflow ? "bg-amber-500" : status === "done" ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${barPct}%` }} />
                  </div>
                )}
              </div>
            );
          })()}
          {planning.assigned_installer && (
            <div className="text-xs text-zinc-500 mt-1">{planning.assigned_installer}</div>
          )}
          {(planning.printing_vendor || planning.material_dispatch_date || planning.expected_delivery_date) && (
            <div className="text-[10px] text-zinc-500 mt-1.5 space-y-0.5 border-t border-zinc-100 pt-1.5" data-testid={`slot-vendor-info-${planning.id}`}>
              {planning.printing_vendor && <div>📦 <span className="font-medium text-zinc-700">{planning.printing_vendor}</span></div>}
              {planning.material_dispatch_date && <div>Dispatch: <span className="font-mono">{planning.material_dispatch_date}</span></div>}
              {planning.expected_delivery_date && <div>Delivery: <span className="font-mono">{planning.expected_delivery_date}</span></div>}
            </div>
          )}
          <div className="flex items-center justify-between mt-2">
            <StatusPill status={planning.resource_status === "available" ? "green" : planning.resource_status === "tentative" ? "amber" : "red"}>
              {planning.resource_status}
            </StatusPill>
            {campaign && <StatusPill status={campaign.health} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewPlanningDialog({ campaigns, pods, onClose, onCreated }) {
  const [form, setForm] = useState({
    campaign_id: campaigns[0]?.campaign_id || "",
    planned_date: new Date().toISOString().slice(0, 10),
    assigned_team: pods[0]?.name || "",
    assigned_installer: "",
    capacity_slot: "full_day",
    route_or_cluster: "",
    resource_status: "available",
    daily_target: 0,
    printing_vendor: "",
    material_dispatch_date: "",
    expected_delivery_date: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const camp = campaigns.find((c) => c.campaign_id === form.campaign_id);
  const remaining = camp ? Math.max((camp.target_installations || 0) - (camp.completed_installations || 0), 0) : 0;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/plannings", { ...form, daily_target: Number(form.daily_target) || 0 });
      toast.success("Slot scheduled");
      onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };
  return (
    <DialogContent className="max-w-lg" data-testid="new-planning-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">Schedule installation slot</DialogTitle>
      </DialogHeader>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <div className="sm:col-span-2">
          <Label className="text-xs">Campaign</Label>
          <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
            <SelectTrigger data-testid="planning-campaign"><SelectValue /></SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_id} — {c.campaign_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {camp && (
            <p className="text-[10px] text-zinc-500 mt-1" data-testid="planning-campaign-remaining">
              Total target: <span className="font-mono font-semibold">{camp.target_installations || 0}</span>
              {" · "}Remaining: <span className="font-mono font-semibold">{remaining}</span>
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs">Pod / Team</Label>
          <Select value={form.assigned_team} onValueChange={(v) => setForm({ ...form, assigned_team: v })}>
            <SelectTrigger data-testid="planning-team"><SelectValue /></SelectTrigger>
            <SelectContent>
              {pods.filter((p) => p.name).map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Planned Date</Label>
          <Input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} data-testid="planning-date" />
        </div>
        <div>
          <Label className="text-xs">Daily target <span className="text-zinc-400 font-normal">(units that day)</span></Label>
          <Input
            type="number"
            min="0"
            value={form.daily_target}
            onChange={(e) => setForm({ ...form, daily_target: e.target.value })}
            data-testid="planning-daily-target"
          />
        </div>
        <div>
          <Label className="text-xs">Slot</Label>
          <Select value={form.capacity_slot} onValueChange={(v) => setForm({ ...form, capacity_slot: v })}>
            <SelectTrigger data-testid="planning-slot"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="afternoon">Afternoon</SelectItem>
              <SelectItem value="full_day">Full Day</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Installer</Label>
          <Input value={form.assigned_installer} onChange={(e) => setForm({ ...form, assigned_installer: e.target.value })} data-testid="planning-installer" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Cluster / Route</Label>
          <Input value={form.route_or_cluster} onChange={(e) => setForm({ ...form, route_or_cluster: e.target.value })} data-testid="planning-cluster" />
        </div>
        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-md">
          <div className="sm:col-span-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Vendor & material</div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Printing Vendor</Label>
            <Input value={form.printing_vendor} onChange={(e) => setForm({ ...form, printing_vendor: e.target.value })} data-testid="planning-printing-vendor" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Material dispatch</Label>
            <Input type="date" value={form.material_dispatch_date} onChange={(e) => setForm({ ...form, material_dispatch_date: e.target.value })} data-testid="planning-dispatch-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Expected delivery</Label>
            <Input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} data-testid="planning-delivery-date" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Resource Status</Label>
          <Select value={form.resource_status} onValueChange={(v) => setForm({ ...form, resource_status: v })}>
            <SelectTrigger data-testid="planning-resource"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="tentative">Tentative</SelectItem>
              <SelectItem value="unavailable">Unavailable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} data-testid="planning-cancel">Cancel</Button>
        <Button onClick={submit} disabled={submitting} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="planning-submit">
          {submitting ? "Saving…" : "Schedule slot"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}


function BulkPlanningDialog({ campaigns, pods, onClose, onCreated }) {
  const today = new Date();
  const oneWeek = new Date(today.getTime() + 6 * 86400000);
  const [form, setForm] = useState({
    campaign_id: campaigns[0]?.campaign_id || "",
    from_date: today.toISOString().slice(0, 10),
    to_date: oneWeek.toISOString().slice(0, 10),
    assigned_team: pods[0]?.name || "",
    assigned_installer: "",
    capacity_slot: "full_day",
    route_or_cluster: "",
    resource_status: "available",
    skip_weekends: true,
    target_mode: "fixed", // 'fixed' | 'distribute'
    daily_target: 0,
    total_to_distribute: 0,
    printing_vendor: "",
    material_dispatch_date: "",
    expected_delivery_date: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const camp = campaigns.find((c) => c.campaign_id === form.campaign_id);
  const remaining = camp ? Math.max((camp.target_installations || 0) - (camp.completed_installations || 0), 0) : 0;

  const dayCount = (() => {
    try {
      const a = new Date(form.from_date);
      const b = new Date(form.to_date);
      let count = 0;
      for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
        if (form.skip_weekends && (d.getDay() === 0 || d.getDay() === 6)) continue;
        count += 1;
      }
      return count;
    } catch { return 0; }
  })();

  const previewPerDay = (() => {
    if (form.target_mode === "distribute") {
      const total = Number(form.total_to_distribute) || 0;
      if (!total || !dayCount) return 0;
      return Math.floor(total / dayCount);
    }
    return Number(form.daily_target) || 0;
  })();
  const previewTotal = form.target_mode === "distribute"
    ? (Number(form.total_to_distribute) || 0)
    : previewPerDay * dayCount;

  const submit = async () => {
    if (!form.campaign_id) { toast.error("Pick a campaign"); return; }
    if (!form.assigned_team) { toast.error("Pick a pod / team"); return; }
    setSubmitting(true);
    try {
      const payload = {
        campaign_id: form.campaign_id,
        from_date: form.from_date,
        to_date: form.to_date,
        assigned_team: form.assigned_team,
        assigned_installer: form.assigned_installer,
        capacity_slot: form.capacity_slot,
        route_or_cluster: form.route_or_cluster,
        resource_status: form.resource_status,
        skip_weekends: form.skip_weekends,
        printing_vendor: form.printing_vendor,
        material_dispatch_date: form.material_dispatch_date,
        expected_delivery_date: form.expected_delivery_date,
      };
      if (form.target_mode === "distribute") {
        payload.total_to_distribute = Number(form.total_to_distribute) || 0;
      } else {
        payload.daily_target = Number(form.daily_target) || 0;
      }
      const { data } = await api.post("/plannings/bulk", payload);
      const baseMsg = `Created ${data.created} planning slots${data.skipped_weekends ? ` (skipped ${data.skipped_weekends} weekend day${data.skipped_weekends > 1 ? "s" : ""})` : ""} · scheduled ${data.total_scheduled} units`;
      if (data.capped) {
        toast.warning(`${baseMsg}. Capped from ${data.capped_from} to fit the campaign's remaining capacity.`);
      } else {
        toast.success(baseMsg);
      }
      onCreated(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };

  return (
    <DialogContent className="max-w-xl" data-testid="bulk-planning-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">Bulk schedule day-wise slots</DialogTitle>
      </DialogHeader>
      <p className="text-xs text-zinc-500 -mt-2">
        Creates one planning slot per day in the date range and assigns a daily target — set a fixed target or split a total across the active days.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <div className="sm:col-span-2">
          <Label className="text-xs">Campaign</Label>
          <Select value={form.campaign_id} onValueChange={(v) => setForm({ ...form, campaign_id: v })}>
            <SelectTrigger data-testid="bulk-campaign"><SelectValue /></SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.campaign_id} — {c.campaign_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {camp && (
            <p className="text-[10px] text-zinc-500 mt-1" data-testid="bulk-campaign-remaining">
              Total target: <span className="font-mono font-semibold">{camp.target_installations || 0}</span>
              {" · "}Remaining: <span className="font-mono font-semibold">{remaining}</span>
            </p>
          )}
        </div>
        <div>
          <Label className="text-xs">From date</Label>
          <Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} data-testid="bulk-from" />
        </div>
        <div>
          <Label className="text-xs">To date</Label>
          <Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="bulk-to" />
        </div>
        <div>
          <Label className="text-xs">Pod / Team</Label>
          <Select value={form.assigned_team} onValueChange={(v) => setForm({ ...form, assigned_team: v })}>
            <SelectTrigger data-testid="bulk-team"><SelectValue /></SelectTrigger>
            <SelectContent>
              {pods.filter((p) => p.name).map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Slot</Label>
          <Select value={form.capacity_slot} onValueChange={(v) => setForm({ ...form, capacity_slot: v })}>
            <SelectTrigger data-testid="bulk-slot"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="afternoon">Afternoon</SelectItem>
              <SelectItem value="full_day">Full Day</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2 border border-zinc-200 rounded-md p-3 bg-zinc-50/50">
          <Label className="text-xs uppercase tracking-wider text-zinc-600 font-semibold">Daily target</Label>
          <div className="flex items-center gap-3 mt-2 text-xs">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="target_mode"
                checked={form.target_mode === "fixed"}
                onChange={() => setForm({ ...form, target_mode: "fixed" })}
                data-testid="bulk-mode-fixed"
              />
              <span>Same target every day</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="target_mode"
                checked={form.target_mode === "distribute"}
                onChange={() => setForm({ ...form, target_mode: "distribute" })}
                data-testid="bulk-mode-distribute"
              />
              <span>Split total across days</span>
            </label>
          </div>
          {form.target_mode === "fixed" ? (
            <div className="mt-2">
              <Label className="text-[10px] text-zinc-500">Units per day</Label>
              <Input
                type="number"
                min="0"
                value={form.daily_target}
                onChange={(e) => setForm({ ...form, daily_target: e.target.value })}
                data-testid="bulk-daily-target"
              />
            </div>
          ) : (
            <div className="mt-2">
              <Label className="text-[10px] text-zinc-500">Total units to split (auto-divided across {dayCount} day{dayCount === 1 ? "" : "s"})</Label>
              <Input
                type="number"
                min="0"
                value={form.total_to_distribute}
                onChange={(e) => setForm({ ...form, total_to_distribute: e.target.value })}
                data-testid="bulk-total-distribute"
              />
              {camp && (
                <button
                  type="button"
                  className="text-[10px] text-blue-600 hover:underline mt-1"
                  onClick={() => setForm({ ...form, total_to_distribute: remaining })}
                  data-testid="bulk-fill-remaining"
                >
                  Use remaining ({remaining})
                </button>
              )}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs">Installer (optional)</Label>
          <Input value={form.assigned_installer} onChange={(e) => setForm({ ...form, assigned_installer: e.target.value })} data-testid="bulk-installer" />
        </div>
        <div>
          <Label className="text-xs">Resource Status</Label>
          <Select value={form.resource_status} onValueChange={(v) => setForm({ ...form, resource_status: v })}>
            <SelectTrigger data-testid="bulk-resource"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="tentative">Tentative</SelectItem>
              <SelectItem value="unavailable">Unavailable</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Cluster / Route (optional)</Label>
          <Input value={form.route_or_cluster} onChange={(e) => setForm({ ...form, route_or_cluster: e.target.value })} data-testid="bulk-cluster" />
        </div>
        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-md">
          <div className="sm:col-span-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Vendor & material (applied to all generated slots)</div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Printing Vendor</Label>
            <Input value={form.printing_vendor} onChange={(e) => setForm({ ...form, printing_vendor: e.target.value })} data-testid="bulk-printing-vendor" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Material dispatch</Label>
            <Input type="date" value={form.material_dispatch_date} onChange={(e) => setForm({ ...form, material_dispatch_date: e.target.value })} data-testid="bulk-dispatch-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Expected delivery</Label>
            <Input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} data-testid="bulk-delivery-date" />
          </div>
        </div>
        <label className="sm:col-span-2 flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={form.skip_weekends}
            onCheckedChange={(v) => setForm({ ...form, skip_weekends: !!v })}
            data-testid="bulk-skip-weekends"
          />
          <span>Skip weekends (Sat / Sun)</span>
        </label>
      </div>
      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 grid grid-cols-3 gap-2 mt-2 text-center">
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Slots</p>
          <p className="font-display text-xl font-bold text-zinc-950" data-testid="bulk-day-count">{dayCount}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">~ Per day</p>
          <p className="font-display text-xl font-bold text-zinc-950" data-testid="bulk-preview-per-day">{previewPerDay}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Total scheduled</p>
          <p className="font-display text-xl font-bold text-zinc-950" data-testid="bulk-preview-total">{previewTotal}</p>
        </div>
      </div>
      <DialogFooter className="mt-2">
        <Button variant="outline" onClick={onClose} data-testid="bulk-cancel">Cancel</Button>
        <Button onClick={submit} disabled={submitting || dayCount === 0} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="bulk-submit">
          {submitting ? "Creating…" : `Create ${dayCount} slot${dayCount === 1 ? "" : "s"}`}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function EditPlanningDialog({ planning, campaigns, pods, onClose, onSaved }) {
  const [form, setForm] = useState({
    planned_date: planning.planned_date || "",
    assigned_team: planning.assigned_team || "",
    assigned_installer: planning.assigned_installer || "",
    capacity_slot: planning.capacity_slot || "full_day",
    route_or_cluster: planning.route_or_cluster || "",
    resource_status: planning.resource_status || "available",
    daily_target: planning.daily_target ?? 0,
    printing_vendor: planning.printing_vendor || "",
    material_dispatch_date: planning.material_dispatch_date || "",
    expected_delivery_date: planning.expected_delivery_date || "",
  });
  const [saving, setSaving] = useState(false);
  const camp = campaigns.find((c) => c.campaign_id === planning.campaign_id);

  const submit = async () => {
    setSaving(true);
    try {
      await api.patch(`/plannings/${planning.id}`, {
        ...form,
        daily_target: Number(form.daily_target) || 0,
      });
      toast.success("Slot updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <DialogContent className="max-w-lg" data-testid="edit-planning-dialog">
      <DialogHeader>
        <DialogTitle className="font-display">
          Edit slot · <span className="font-mono text-sm text-zinc-500">{planning.campaign_id}</span>
        </DialogTitle>
      </DialogHeader>
      {camp && <p className="text-xs text-zinc-500 -mt-2">{camp.campaign_name}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
        <div>
          <Label className="text-xs">Planned Date</Label>
          <Input type="date" value={form.planned_date} onChange={(e) => setForm({ ...form, planned_date: e.target.value })} data-testid="edit-planning-date" />
        </div>
        <div>
          <Label className="text-xs">Daily target</Label>
          <Input
            type="number"
            min="0"
            value={form.daily_target}
            onChange={(e) => setForm({ ...form, daily_target: e.target.value })}
            data-testid="edit-planning-daily-target"
          />
        </div>
        <div>
          <Label className="text-xs">Slot</Label>
          <Select value={form.capacity_slot} onValueChange={(v) => setForm({ ...form, capacity_slot: v })}>
            <SelectTrigger data-testid="edit-planning-slot"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning</SelectItem>
              <SelectItem value="afternoon">Afternoon</SelectItem>
              <SelectItem value="full_day">Full Day</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Pod / Team</Label>
          <Select value={form.assigned_team || undefined} onValueChange={(v) => setForm({ ...form, assigned_team: v })}>
            <SelectTrigger data-testid="edit-planning-team"><SelectValue placeholder="Pick…" /></SelectTrigger>
            <SelectContent>
              {pods.filter((p) => p.name).map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Installer</Label>
          <Input value={form.assigned_installer} onChange={(e) => setForm({ ...form, assigned_installer: e.target.value })} data-testid="edit-planning-installer" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Cluster / Route</Label>
          <Input value={form.route_or_cluster} onChange={(e) => setForm({ ...form, route_or_cluster: e.target.value })} data-testid="edit-planning-cluster" />
        </div>
        <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 bg-zinc-50 border border-zinc-200 rounded-md">
          <div className="sm:col-span-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Vendor & material</div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Printing Vendor</Label>
            <Input value={form.printing_vendor} onChange={(e) => setForm({ ...form, printing_vendor: e.target.value })} data-testid="edit-planning-printing-vendor" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Material dispatch</Label>
            <Input type="date" value={form.material_dispatch_date} onChange={(e) => setForm({ ...form, material_dispatch_date: e.target.value })} data-testid="edit-planning-dispatch-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Expected delivery</Label>
            <Input type="date" value={form.expected_delivery_date} onChange={(e) => setForm({ ...form, expected_delivery_date: e.target.value })} data-testid="edit-planning-delivery-date" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Resource Status</Label>
          <Select value={form.resource_status} onValueChange={(v) => setForm({ ...form, resource_status: v })}>
            <SelectTrigger data-testid="edit-planning-resource"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="tentative">Tentative</SelectItem>
              <SelectItem value="unavailable">Unavailable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onClose} data-testid="edit-planning-cancel">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="edit-planning-submit">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

